"use strict";
/**
 * Storefront rules that both the Mini App and the API need to agree on.
 *
 * Kept pure and shared for the same reason the Zod schemas are: the product
 * sheet decides whether "Savatga qo'shish" is enabled with the very function
 * the tests pin, so the UI cannot quietly accept a selection the server will
 * refuse. The server still re-validates everything — this is the courtesy
 * check, not the control.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isRequiredGroup = isRequiredGroup;
exports.isSingleChoiceGroup = isSingleChoiceGroup;
exports.needsConfiguration = needsConfiguration;
exports.defaultModifierIds = defaultModifierIds;
exports.defaultVariantId = defaultVariantId;
exports.validateModifierSelection = validateModifierSelection;
exports.requirementLabel = requirementLabel;
exports.cartLineKey = cartLineKey;
exports.estimateUnitPrice = estimateUnitPrice;
exports.formatPortion = formatPortion;
exports.missingProfileFields = missingProfileFields;
exports.fullNameOf = fullNameOf;
/* -------------------------------------------------------------------------- */
/* Modifiers                                                                  */
/* -------------------------------------------------------------------------- */
/** A group is required when it demands at least one choice. */
function isRequiredGroup(group) {
    return group.minSelect >= 1;
}
/** One choice at most renders as radio buttons; more as checkboxes. */
function isSingleChoiceGroup(group) {
    return group.maxSelect === 1;
}
/**
 * Whether tapping "+" on a card can add the product straight to the cart.
 *
 * Not when there is a size to pick (more than one variant) or any group the
 * customer must answer — even if its defaults would satisfy it: a required
 * choice is one the restaurant wants the customer to actually make.
 */
function needsConfiguration(product) {
    return product.variants.length > 1 || product.modifierGroups.some(isRequiredGroup);
}
/**
 * The selection a sheet opens with, and what a quick add sends.
 *
 * The restaurant's defaults, capped at each group's maximum so a
 * misconfigured group with three defaults and `maxSelect: 1` cannot produce a
 * selection the server rejects.
 */
function defaultModifierIds(groups) {
    const chosen = [];
    for (const group of groups) {
        const defaults = group.modifiers.filter((modifier) => modifier.isDefault);
        chosen.push(...defaults.slice(0, Math.max(0, group.maxSelect)).map((modifier) => modifier.id));
    }
    return chosen;
}
/** The variant a sheet opens with: the restaurant's default, else the first. */
function defaultVariantId(product) {
    const variant = product.variants.find((entry) => entry.isDefault) ?? product.variants[0];
    return variant?.id ?? null;
}
/**
 * Every group whose current selection the server would refuse, with the
 * Uzbek line the sheet shows under it. Empty means the selection is valid.
 *
 * Ids that belong to no group of this product are reported against the
 * product (`groupId: ''`) — the server rejects those too.
 */
function validateModifierSelection(groups, selectedIds) {
    const selected = new Set(selectedIds);
    const issues = [];
    for (const group of groups) {
        const count = group.modifiers.filter((modifier) => selected.has(modifier.id)).length;
        for (const modifier of group.modifiers)
            selected.delete(modifier.id);
        if (count < group.minSelect) {
            issues.push({ groupId: group.id, message: requirementLabel(group) });
        }
        else if (count > group.maxSelect) {
            issues.push({
                groupId: group.id,
                message: `Koʻpi bilan ${group.maxSelect} ta tanlash mumkin`,
            });
        }
    }
    if (selected.size > 0) {
        issues.push({ groupId: '', message: 'Tanlangan variant bu mahsulotga tegishli emas' });
    }
    return issues;
}
/** The small grey line under a group's name: what the customer must do. */
function requirementLabel(group) {
    const { minSelect: min, maxSelect: max } = group;
    if (min >= 1) {
        if (min === max)
            return min === 1 ? 'Bittasini tanlang' : `${min} tasini tanlang`;
        return `${min} tadan ${max} tagacha tanlang`;
    }
    return max > 1 ? `Ixtiyoriy, ${max} tagacha` : 'Ixtiyoriy';
}
/* -------------------------------------------------------------------------- */
/* Cart lines                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Identifies one configuration of a product in a cart.
 *
 * The modifier ids are sorted, so ticking cheese then sauce and sauce then
 * cheese is the same line — while the same burger with and without cheese
 * stays two lines, which is the point: merging them would charge the wrong
 * price for one of them.
 */
function cartLineKey(productId, variantId, modifierIds) {
    return [productId, variantId ?? 'base', ...[...modifierIds].sort()].join('|');
}
/**
 * A client-side estimate of one unit, for the running total on screen.
 *
 * Mirrors the server: variant delta clamped at zero, modifiers added per
 * unit. It is only ever displayed — what the customer is charged comes back
 * from the server's own pricing.
 */
function estimateUnitPrice(product, variantId, modifierIds) {
    const variant = variantId ? product.variants.find((entry) => entry.id === variantId) : undefined;
    const base = Math.max(0, product.price + (variant?.priceDelta ?? 0));
    const wanted = new Set(modifierIds);
    let extras = 0;
    for (const group of product.modifierGroups) {
        for (const modifier of group.modifiers) {
            if (wanted.has(modifier.id))
                extras += modifier.price;
        }
    }
    return base + extras;
}
/* -------------------------------------------------------------------------- */
/* Display                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * `450 g`, `1,2 kg`, `300 ml`, `1,5 l`, `6 dona`.
 *
 * A comma decimal, because that is how Uzbek menus print it.
 */
function formatPortion(size, unit) {
    if (size == null || unit == null || size <= 0)
        return null;
    const scaled = (value) => (Math.round(value * 10) / 10).toString().replace('.', ',');
    switch (unit) {
        case 'G':
            return size >= 1000 ? `${scaled(size / 1000)} kg` : `${size} g`;
        case 'ML':
            return size >= 1000 ? `${scaled(size / 1000)} l` : `${size} ml`;
        case 'PCS':
            return `${size} dona`;
        default:
            return null;
    }
}
/* -------------------------------------------------------------------------- */
/* Profile                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Which of the three fields an order needs are still empty.
 *
 * Whitespace counts as empty: a name of three spaces is not a name a courier
 * can ask for at the door.
 */
function missingProfileFields(profile) {
    const missing = [];
    if (!profile.firstName?.trim())
        missing.push('firstName');
    if (!profile.lastName?.trim())
        missing.push('lastName');
    if (!profile.phone?.trim())
        missing.push('phone');
    return missing;
}
/** `Elbek Qurbonov`, or `null` when neither part is known. */
function fullNameOf(profile) {
    const name = [profile.firstName?.trim(), profile.lastName?.trim()].filter(Boolean).join(' ');
    return name || null;
}
//# sourceMappingURL=storefront.js.map